/**
 * ErrorBoundary
 * React class component that catches JavaScript errors in child components.
 * Displays a luxury-themed fallback UI with Retry, Go to Dashboard, and Reload buttons.
 * Logs errors to the console and optionally to a backend endpoint.
 */
import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Box, Typography, Button, Paper, Collapse } from '@mui/material';
import {
  ErrorOutline as ErrorIcon,
  Refresh as RefreshIcon,
  Dashboard as DashboardIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';

// ---- Types ----

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Optional custom fallback component. */
  fallback?: ReactNode;
  /** Optional callback invoked when an error is caught. */
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  showDetails: boolean;
}

// ---- Styles ----

const GOLD = '#C9A96E';
const DARK_BG = '#1a1a1a';

// ---- Helpers ----

/**
 * Attempt to log the error to a backend endpoint.
 * Fire-and-forget; failures are silently ignored.
 */
function logErrorToBackend(error: Error, errorInfo: ErrorInfo): void {
  try {
    const API_BASE_URL = process.env.REACT_APP_API_URL || '';
    const payload = {
      error: error.toString(),
      stack: error.stack || '',
      componentStack: errorInfo.componentStack || '',
      url: window.location.href,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
    };
    // Use navigator.sendBeacon for reliability (does not block unload)
    if (navigator.sendBeacon) {
      navigator.sendBeacon(
        `${API_BASE_URL}/api/v1/chatbot/log-error`,
        new Blob([JSON.stringify(payload)], { type: 'application/json' })
      );
    } else {
      fetch(`${API_BASE_URL}/api/v1/chatbot/log-error`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        keepalive: true,
      }).catch(() => {});
    }
  } catch {
    // Swallow -- logging must never break the boundary
  }
}

// ---- Component ----

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: false,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    this.setState({ errorInfo });

    // Always log to console
    console.error('[ErrorBoundary] Caught error:', error, errorInfo);

    // Log to backend (fire-and-forget)
    logErrorToBackend(error, errorInfo);

    // Notify parent callback if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  handleRetry = (): void => {
    this.setState({ hasError: false, error: null, errorInfo: null, showDetails: false });
  };

  handleGoToDashboard = (): void => {
    // Clear error state and navigate to dashboard
    this.setState({ hasError: false, error: null, errorInfo: null, showDetails: false });
    window.location.href = '/dashboard';
  };

  handleReload = (): void => {
    window.location.reload();
  };

  toggleDetails = (): void => {
    this.setState((prev) => ({ showDetails: !prev.showDetails }));
  };

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '60vh',
            p: 3,
          }}
        >
          <Paper
            elevation={6}
            sx={{
              maxWidth: 560,
              width: '100%',
              p: 5,
              textAlign: 'center',
              backgroundColor: '#ffffff',
              borderTop: `4px solid ${GOLD}`,
              borderRadius: 2,
            }}
          >
            <ErrorIcon
              sx={{
                fontSize: 64,
                color: GOLD,
                mb: 2,
              }}
            />

            <Typography
              variant="h5"
              sx={{
                fontWeight: 600,
                color: DARK_BG,
                mb: 1,
              }}
            >
              Something went wrong
            </Typography>

            <Typography
              variant="body1"
              sx={{
                color: '#666',
                mb: 3,
                lineHeight: 1.6,
              }}
            >
              An unexpected error occurred while rendering this section.
              You can retry, go back to the dashboard, or reload the page.
            </Typography>

            {/* Error details -- visible in all environments via toggle */}
            {this.state.error && (
              <Box sx={{ mb: 3 }}>
                <Button
                  size="small"
                  onClick={this.toggleDetails}
                  endIcon={this.state.showDetails ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  sx={{ color: '#999', textTransform: 'none', mb: 1 }}
                >
                  {this.state.showDetails ? 'Hide details' : 'Show error details'}
                </Button>
                <Collapse in={this.state.showDetails}>
                  <Box
                    sx={{
                      textAlign: 'left',
                      p: 2,
                      backgroundColor: '#f5f5f5',
                      borderRadius: 1,
                      maxHeight: 200,
                      overflow: 'auto',
                    }}
                  >
                    <Typography
                      variant="caption"
                      component="pre"
                      sx={{
                        fontFamily: 'monospace',
                        fontSize: '0.75rem',
                        color: '#c62828',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                      }}
                    >
                      {this.state.error.toString()}
                      {this.state.errorInfo?.componentStack && (
                        <>
                          {'\n\nComponent stack:'}
                          {this.state.errorInfo.componentStack}
                        </>
                      )}
                    </Typography>
                  </Box>
                </Collapse>
              </Box>
            )}

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                startIcon={<RefreshIcon />}
                onClick={this.handleRetry}
                sx={{
                  backgroundColor: GOLD,
                  color: DARK_BG,
                  fontWeight: 600,
                  '&:hover': {
                    backgroundColor: '#c9a432',
                  },
                }}
              >
                Retry
              </Button>
              <Button
                variant="contained"
                startIcon={<DashboardIcon />}
                onClick={this.handleGoToDashboard}
                sx={{
                  backgroundColor: DARK_BG,
                  color: '#fff',
                  fontWeight: 600,
                  '&:hover': {
                    backgroundColor: '#333',
                  },
                }}
              >
                Go to Dashboard
              </Button>
              <Button
                variant="outlined"
                onClick={this.handleReload}
                sx={{
                  borderColor: DARK_BG,
                  color: DARK_BG,
                  '&:hover': {
                    borderColor: GOLD,
                    color: GOLD,
                  },
                }}
              >
                Reload Page
              </Button>
            </Box>
          </Paper>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
